import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  MenuItem,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, LocalHospital } from '@mui/icons-material';
import { consultationsAPI, animauxAPI } from '../../services/api';

const ConsultationsPage = () => {
  const [consultations, setConsultations] = useState([]);
  const [animaux, setAnimaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentConsultation, setCurrentConsultation] = useState(null);
  const [formData, setFormData] = useState({
    date_consultation: new Date().toISOString().split('T')[0],
    motif: '',
    diagnostic: '',
    traitement: '',
    montant: '',
    id_animal: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [consultationsResponse, animauxResponse] = await Promise.all([
        consultationsAPI.getAll(),
        animauxAPI.getAll()
      ]);
      setConsultations(consultationsResponse.data.data || []);
      setAnimaux(animauxResponse.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (consultation = null) => {
    if (consultation) {
      setEditMode(true);
      setCurrentConsultation(consultation);
      setFormData({
        date_consultation: consultation.date_consultation ? new Date(consultation.date_consultation).toISOString().split('T')[0] : '',
        motif: consultation.motif || '',
        diagnostic: consultation.diagnostic || '',
        traitement: consultation.traitement || '',
        montant: consultation.montant || '',
        id_animal: consultation.id_animal || ''
      });
    } else {
      setEditMode(false);
      setCurrentConsultation(null);
      setFormData({
        date_consultation: new Date().toISOString().split('T')[0],
        motif: '',
        diagnostic: '',
        traitement: '',
        montant: '',
        id_animal: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentConsultation(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        montant: parseFloat(formData.montant) || 0
      };

      if (editMode && currentConsultation) {
        await consultationsAPI.update(currentConsultation.id_consultation, dataToSend);
        showSnackbar('Consultation modifiée avec succès', 'success');
      } else {
        await consultationsAPI.create(dataToSend);
        showSnackbar('Consultation créée avec succès', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette consultation ?')) {
      try {
        await consultationsAPI.delete(id);
        showSnackbar('Consultation supprimée avec succès', 'success');
        loadData();
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getAnimalInfo = (idAnimal) => {
    const animal = animaux.find(a => a.id_animal === idAnimal);
    if (!animal) return 'N/A';
    const client = animal.Client;
    return `${animal.nom_animal} (${client?.nom || ''} ${client?.prenom || ''})`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <LocalHospital sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Consultations
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={animaux.length === 0}
        >
          Nouvelle Consultation
        </Button>
      </Box>

      {animaux.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Vous devez d'abord créer des animaux avant d'ajouter des consultations.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Animal</strong></TableCell>
              <TableCell><strong>Motif</strong></TableCell>
              <TableCell><strong>Montant</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {consultations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">
                    Aucune consultation trouvée. Cliquez sur "Nouvelle Consultation" pour en ajouter une.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              consultations.map((consultation) => (
                <TableRow key={consultation.id_consultation} hover>
                  <TableCell>{consultation.id_consultation}</TableCell>
                  <TableCell>{formatDate(consultation.date_consultation)}</TableCell>
                  <TableCell>{getAnimalInfo(consultation.id_animal)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {consultation.motif}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${consultation.montant || 0} €`} 
                      color="success" 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleOpenDialog(consultation)}
                      title="Modifier"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => handleDelete(consultation.id_consultation)}
                      title="Supprimer"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier la consultation' : 'Nouvelle consultation'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              name="date_consultation"
              label="Date de consultation"
              type="date"
              fullWidth
              margin="normal"
              value={formData.date_consultation}
              onChange={handleChange}
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              name="id_animal"
              label="Animal"
              fullWidth
              margin="normal"
              value={formData.id_animal}
              onChange={handleChange}
              required
              select
            >
              {animaux.map((animal) => (
                <MenuItem key={animal.id_animal} value={animal.id_animal}>
                  {animal.nom_animal} - {animal.Client?.nom} {animal.Client?.prenom}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="motif"
              label="Motif de la consultation"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={formData.motif}
              onChange={handleChange}
              required
              placeholder="Vaccination, bilan de santé, symptômes..."
            />
            <TextField
              name="diagnostic"
              label="Diagnostic"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={formData.diagnostic}
              onChange={handleChange}
              placeholder="Observations et diagnostic..."
            />
            <TextField
              name="traitement"
              label="Traitement prescrit"
              fullWidth
              margin="normal"
              multiline
              rows={2}
              value={formData.traitement}
              onChange={handleChange}
              placeholder="Médicaments, recommandations..."
            />
            <TextField
              name="montant"
              label="Montant (€)"
              type="number"
              fullWidth
              margin="normal"
              value={formData.montant}
              onChange={handleChange}
              inputProps={{ step: "0.01", min: "0" }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ConsultationsPage;